import React, { useMemo, useState } from "react";
import Router from "next/router";
import { TextInput } from "@mantine/core";
import { Spotlight, spotlight } from "@mantine/spotlight";
import { IconSearch } from "@tabler/icons-react";
import styles from "./SearchForm.module.scss";

// Utility function to remove duplicates based on a given key (course title)
function removeDuplicates(arr, key) {
  const seen = new Set();
  return arr.reduce((acc, item) => {
    const normalized = item[key].trim().toLowerCase();
    if (!seen.has(normalized)) {
      seen.add(normalized);
      acc.push(item);
    }
    return acc;
  }, []);
}

const SearchForm = ({ courses, teachers }) => {
  // State to store current query and number of filtered actions
  const [filteredCount, setFilteredCount] = useState(0);
  const [queryValue, setQueryValue] = useState("");

  // Deduplicate courses by title
  const uniqueCourses = useMemo(() => removeDuplicates(courses, "title"), [courses]);

  // Map each unique course to a Spotlight action
  const courseActions = uniqueCourses.map((course) => ({
    id: `course-${course.id}`,
    label: course.title,
    description: "Course",
    onClick: () => Router.push({ pathname: `/courses/${course.id}`, query: { s: `${Date.now()}` } }),
  }));

  // Map each teacher to a Spotlight action
  const teacherActions = useMemo(
    () =>
      teachers.map((teacher) => ({
        id: `teacher-${teacher.profileId}`,
        label: teacher.name,
        description: "Teacher",
        onClick: () =>
          Router.push({
            pathname: `/user/profile/${teacher.profileId}`,
            query: { s: `${Date.now()}` },
          }),
        leftSection: teacher.profilePhoto ? (
          <img src={teacher.profilePhoto} alt={teacher.name} width={34} height={34} style={{ borderRadius: "50%" }} />
        ) : null,
      })),
    [teachers]
  );

  // Create a sticky action that is always visible at the top if a query is provided
  const stickyAction = useMemo(() => {
    if (queryValue.trim() === "") return null;
    return {
      id: "search-all",
      label: `Search for "${queryValue}"`,
      description: "Show all results",
      onClick: () => {
        Router.push({ pathname: "/search", query: { q: queryValue.trim() } });
        spotlight.close();
      },
    };
  }, [queryValue]);

  // Combine course and teacher actions, adding the sticky action at the beginning if it exists
  const actions = useMemo(
    () => (stickyAction ? [stickyAction, ...courseActions, ...teacherActions] : [...courseActions, ...teacherActions]),
    [stickyAction, courseActions, teacherActions]
  );

  // Pure filter function that just filters actions based on the query
  const customFilter = (query, actions) => {
    return actions.filter(
      (action) =>
        action.alwaysVisible === true ||
        action.label.toLowerCase().includes(query.toLowerCase()) ||
        (action.description && action.description.toLowerCase().includes(query.toLowerCase()))
    );
  };

  return (
    <>
      <div className={styles.searchFormContainer}>
        <TextInput
          placeholder="Search..."
          leftSection={<IconSearch size={20} stroke={1.5} />}
          onClick={spotlight.open}
          readOnly
          classNames={{ input: styles.searchBarButton }}
        />
      </div>
      <Spotlight
        actions={actions}
        filter={customFilter}
        onQueryChange={(query) => {
          setQueryValue(query);
          const filtered = actions.filter(
            (action) =>
              action.alwaysVisible === true ||
              action.label.toLowerCase().includes(query.toLowerCase()) ||
              (action.description && action.description.toLowerCase().includes(query.toLowerCase()))
          );
          setFilteredCount(filtered.length);
        }}
        nothingFound="No results found"
        maxHeight={350}
        scrollable
        highlightQuery
        searchProps={{
          leftSection: <IconSearch size={20} stroke={1.5} />,
          placeholder: "Search...",
        }}
      />
    </>
  );
};

export default SearchForm;
